<template>
  <div class="qv26">
    <button class="qv26__close" type="button" aria-label="Close quick view" @click="closeQuickView">
      <svg class="qv26__close-icon">
        <use href="#close"></use>
      </svg>
    </button>

    <QuickView2026Images
      :product="product"
      :selectedColor="productForm.Color"
      :selectedTown="productForm.Town"
      :productForm="productForm"
      :firstOption="firstOption"
      :showWishlistIcon="showWishlistIcon"
    />

    <div class="qv26__content">
      <h2 class="qv26__title">{{ product.title }}</h2>

      <div v-if="product.id" class="qv26__reviews">
        <div class="yotpo-widget-instance" data-yotpo-instance-id="498880" :data-yotpo-product-id="product.id"></div>
      </div>

      <div v-if="!isSelectedOptionEmpty" class="qv26__price">
        <div class="pm__price-wrapper">
          <div v-if="customerDiscountVal > 0 && discountPrice !== actualPrice" class="pm__price-seperator">
            <span class="pm__compare-price">{{ actualPrice }}</span>
            <span :class="['pm__price', { bold: productMinPrice !== productMaxPrice || customerDiscountVal > 0 || discountPrice !== actualPrice }]">
              {{ discountPrice }}
            </span>
            <PriceSaveText :compare-price="actualPriceValue" :current-price="discountPriceValue" />
          </div>
          <div v-if="discountPrice !== actualPrice && customerDiscountVal === 0" class="pm__price-seperator">
            <span class="pm__compare-price">{{ compareAtPrice }}</span>
            <span :class="['pm__price', { bold: productMinPrice !== productMaxPrice || customerDiscountVal > 0 || discountPrice !== actualPrice }]">
              {{ discountPrice }}
            </span>
            <PriceSaveText :compare-price="compareAtPriceValue" :current-price="discountPriceValue" />
          </div>
          <div v-if="discountPrice === actualPrice" :class="['pm__price', { bold: productMinPrice !== productMaxPrice || customerDiscountVal > 0 || discountPrice !== actualPrice }]">
            {{ discountPrice }}
          </div>
        </div>
      </div>

      <ProductOptions
        :product="product"
        :selectedOptions="productForm"
        :isSecondTurn="isSecondTurn"
        :showSecondTurnSelectors="true"
        :hideUnavailableOption="false"
        :isImageTypeSwatch="true"
        @on-option-change="onOptionChange"
      />

      <div class="qv26__atc" data-stio-gtm-id="Quickview2026: ATC CTA">
        <ProductATC
          :product="product"
          :productForm="productForm"
          :selectedVariant="productForm.selectedVariant"
          :selectedFirstOption="selectedFirstLevelOption"
        />
      </div>

      <a :href="productDetailsUrl" class="qv26__details-link">View Full Product Details</a>
    </div>
  </div>
</template>

<script setup>
  import { computed, onBeforeMount, onMounted, reactive, ref, toRefs, watch } from 'vue';
  import { useStore } from 'vuex';
  import PriceSaveText from '../global/price-save-text.vue';
  import ProductOptions from '../pdp-first-fold/components/product-options/index.vue';
  import ProductATC from '../pdp-first-fold/components/product-atc.vue';
  import QuickView2026Images from './quick-view-2026-images.vue';
  import { getSecondTurnDefaultVariant, getSecondTurnVariantForOption } from '../../utils/second-turn';

  const props = defineProps({
    product: {
      type: Object,
      required: true,
    },
    selectedColor: {
      type: String,
      default: null,
    },
    showWishlistIcon: {
      type: Boolean,
      default: true,
    },
  });

  const { product, selectedColor } = toRefs(props);
  const store = useStore();
  const emit = defineEmits(['heading', 'component-updated']);
  const customerDiscountVal = ref(0);

  const productForm = reactive({
    selectedVariant: null,
  });

  const firstOption = product.value.options[0].name;

  onBeforeMount(() => {
    emit('heading', 'Quickview');
    getCustomerDiscount();
    initialSetup();
    window.refreshYotpoData();
  });

  onMounted(() => {
    emit('component-updated');
  });

  const selectedFirstLevelOption = computed(() => {
    return store.state.pdp.firstLevelOptions?.[product.value.id];
  });

  const isSelectedOptionEmpty = computed(() => Object.keys(selectedFirstLevelOption.value || {}).length === 0);

  const discountPriceValue = computed(() => discountPriceLogic(selectedFirstLevelOption.value || {}));

  const discountPrice = computed(() => {
    return window.formattedPrice(discountPriceValue.value);
  });

  const actualPriceValue = computed(() => {
    if (selectedFirstLevelOption.value?.compare_at_price > 0) {
      return selectedFirstLevelOption.value.compare_at_price;
    }

    return selectedFirstLevelOption.value?.price;
  });

  const actualPrice = computed(() => {
    return window.formattedPrice(actualPriceValue.value);
  });

  const compareAtPriceValue = computed(() => selectedFirstLevelOption.value?.compare_at_price);

  const compareAtPrice = computed(() => {
    return window.formattedPrice(compareAtPriceValue.value);
  });

  const productMinPrice = computed(() => {
    return window.formattedPrice(product.value.min_price / 100);
  });

  const productMaxPrice = computed(() => {
    return window.formattedPrice(product.value.max_price / 100);
  });

  const isSecondTurn = computed(() => {
    return product.value.tags.includes('Second Turn');
  });

  const productDetailsUrl = computed(() => {
    const { handle } = product.value;

    if (isSecondTurn.value && productForm.selectedVariant) {
      return `/products/${handle}?variant=${productForm.selectedVariant.id}`;
    }

    return `/products/${handle}`;
  });

  const additionalPropertyOptions = computed(() => {
    const additionalOpt = product.value?.additional_property_options;

    if (!additionalOpt) return [];

    if (typeof additionalOpt === 'string') {
      return additionalOpt.trim() ? additionalOpt.split(',') : [];
    }

    if (Array.isArray(additionalOpt)) {
      return additionalOpt.map((opt) => opt.value);
    }

    return [];
  });

  const initialSetup = () => {
    const { options, variants } = product.value;

    if (isSecondTurn.value || options.length === 1) {
      productForm.selectedVariant = isSecondTurn.value
        ? getSecondTurnDefaultVariant(product.value, selectedColor.value)
        : variants[0];
      options.forEach((option, index) => {
        productForm[option.name] = productForm.selectedVariant[`option${index + 1}`];
      });
    } else {
      if (selectedColor.value) {
        productForm.Color = selectedColor.value;
      } else {
        watchSelectedFirstLevelOptionOnceReady();
      }

      if (additionalPropertyOptions.value.length) {
        const inseamOutseamData = options.find((ele) => ele.name === 'Inseam' || ele.name === 'Outseam');
        if (inseamOutseamData) {
          productForm[inseamOutseamData.name] = inseamOutseamData.values[0];
        }
      }
    }

    updateVariantValue();
  };

  const onOptionChange = (key, value) => {
    const secondTurnVariant =
      isSecondTurn.value && getSecondTurnVariantForOption(product.value, productForm, key, value);

    if (secondTurnVariant) {
      product.value.options.forEach((option, index) => {
        productForm[option.name] = secondTurnVariant[`option${index + 1}`];
      });
    } else {
      productForm[key] = value;
    }

    updateVariantValue();
  };

  const updateVariantValue = () => {
    let variantValue = '';
    const { options } = product.value;

    options.forEach((option, index, array) => {
      variantValue += productForm[option.name];
      if (index !== array.length - 1) {
        variantValue += ' / ';
      }
    });

    updateSelectedVariant(variantValue);
  };

  const updateSelectedVariant = (value) => {
    const variant = product.value.variants.find((item) => item.value === value);
    productForm.selectedVariant = variant || null;
  };

  const discountPriceLogic = (data) => {
    if (!data?.price) return 0;

    if (data.compare_at_price > 0) {
      if (data.compare_at_price * (1 - customerDiscountVal.value) < data.price) {
        return data.compare_at_price * (1 - customerDiscountVal.value);
      }

      return data.price;
    }

    return data.price * (1 - customerDiscountVal.value);
  };

  const watchSelectedFirstLevelOptionOnceReady = () => {
    const unwatch = watch(selectedFirstLevelOption, (newVal) => {
      if (newVal?.value) {
        productForm[firstOption] = newVal.value;
        unwatch();
      }
    });
  };

  const getCustomerDiscount = () => {
    customerDiscountVal.value = window.customerDiscount(product.value.tags);
  };

  const closeQuickView = () => {
    window.toggleDrawer(false, 'QuickView2026');
  };
</script>
